import { Link } from "react-router-dom";
import { ArrowRight, Eye, Sparkles, Wrench, Check, Instagram, Sun } from "lucide-react";
import { ICON_SIZE, VIDEO } from "../constants/ui";

const ASSETS = {
  heroVideo: "https://customer-assets.emergentagent.com/job_lunettes-sur-mesure/artifacts/ldo5yjxo_copy_708ADFD0-3052-4C9A-844D-2C131348613C.mov",
  store_interior: "https://customer-assets.emergentagent.com/job_lunettes-sur-mesure/artifacts/h4mnfolp_IMG_1752.jpeg",
  gucci: "https://customer-assets.emergentagent.com/job_lunettes-sur-mesure/artifacts/6hkyxyh2_IMG_2721%20%281%29.PNG",
  cartier: "https://customer-assets.emergentagent.com/job_lunettes-sur-mesure/artifacts/xe492apu_IMG_2737.PNG",
  collection: "https://customer-assets.emergentagent.com/job_lunettes-sur-mesure/artifacts/2qz8fi98_IMG_2642.jpeg",
  hero: "https://customer-assets.emergentagent.com/job_lunettes-sur-mesure/artifacts/ea8cot3l_IMG_2813.PNG",
};

export default function HomePage() {
  return (
    <div data-testid="home-page" className="bg-[#F9F9F7]">
      {/* Hero Section - Split Layout */}
      <section data-testid="hero-section" className="min-h-screen bg-[#F9F9F7]">
        <div className="container-premium min-h-screen flex items-center">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center w-full pt-28 pb-16 lg:py-0">
            {/* Left - Content */}
            <div className="order-2 lg:order-1">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-[#F0F0EB] px-4 py-2 rounded-full mb-6">
                <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse"></span>
                <span className="text-xs uppercase tracking-[0.15em] text-[#666660] font-medium">
                  Opticien Premium à Vaires-sur-Marne
                </span>
              </div>

              {/* Title */}
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] mb-6 text-[#1A1A1A]">
                Votre regard,{" "}
                <span className="text-[#D4AF37]">notre expertise</span>
              </h1>

              {/* Description */}
              <p className="text-lg font-light text-[#666660] leading-relaxed mb-8 max-w-lg">
                Lunettes solaires, optiques et créations sur mesure. 
                Découvrez une expérience unique alliant grandes marques et personnalisation d&apos;exception.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link
                  to="/personnalisation"
                  data-testid="hero-cta-realisations"
                  className="inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#1A1A1A] px-8 py-4 text-xs uppercase tracking-[0.15em] font-medium hover:bg-[#C4A030] transition-colors duration-300"
                >
                  Nos Réalisations
                  <ArrowRight size={16} />
                </Link>
                <Link
                  to="/catalogue"
                  data-testid="hero-cta-catalogue"
                  className="inline-flex items-center justify-center gap-3 border-2 border-[#1A1A1A] text-[#1A1A1A] px-8 py-4 text-xs uppercase tracking-[0.15em] font-medium hover:bg-[#1A1A1A] hover:text-white transition-colors duration-300"
                >
                  Notre Catalogue
                </Link>
              </div>

              {/* Two specialties badges */}
              <div className="flex flex-wrap gap-4">
                <div className="inline-flex items-center gap-3 bg-[#F0F0EB] px-5 py-3">
                  <Sun size={20} className="text-[#D4AF37]" />
                  <span className="text-sm text-[#1A1A1A]">Solaires & Optiques</span>
                </div>
                <div className="inline-flex items-center gap-3 bg-[#F0F0EB] px-5 py-3">
                  <Sparkles size={20} className="text-[#D4AF37]" />
                  <span className="text-sm text-[#1A1A1A]">Montures Percées</span>
                </div>
              </div>
            </div>

            {/* Right - Video */}
            <div className="order-1 lg:order-2">
              <div className="relative">
                <video
                  src={ASSETS.heroVideo}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-[400px] lg:h-[650px] object-cover rounded-3xl shadow-2xl pointer-events-none"
                />
                {/* Decorative elements */}
                <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-[#D4AF37]/10 rounded-full -z-10"></div>
                <div className="absolute -top-4 -right-4 w-32 h-32 bg-[#D4AF37]/5 rounded-full -z-10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two Universes Section */}
      <section data-testid="universes-section" className="py-20 bg-[#F9F9F7]">
        <div className="container-premium">
          <div className="text-center mb-14">
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] mb-3 font-medium">Deux Univers</p>
            <h2 className="font-serif text-3xl md:text-4xl font-normal tracking-tight text-[#1A1A1A]">
              Choisissez votre expérience
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Collections - Sunglasses & Classic - Coming Soon */}
            <div 
              className="group relative overflow-hidden bg-[#1A1A1A] rounded-2xl aspect-[4/3] flex items-end p-8 cursor-not-allowed opacity-80"
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent z-10" />
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1511499767150-a48a237f0083?w=800')] bg-cover bg-center opacity-50" />
              <div className="relative z-20">
                <Sun className="text-[#D4AF37] mb-4" size={36} />
                <h3 className="font-serif text-3xl text-white mb-2">Nos Collections</h3>
                <p className="text-white/70 font-light mb-4">Lunettes solaires & optiques des plus grandes marques</p>
                <span className="inline-flex items-center gap-2 text-[#D4AF37]/70 text-sm uppercase tracking-wider">
                  À venir
                </span>
              </div>
            </div>

            {/* Sur Mesure - Customization */}
            <Link 
              to="/personnalisation"
              className="group relative overflow-hidden bg-[#1A1A1A] rounded-2xl aspect-[4/3] flex items-end p-8 cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent z-10" />
              <div className="absolute inset-0 bg-[url('https://customer-assets.emergentagent.com/job_lunettes-sur-mesure/artifacts/h4mnfolp_IMG_1752.jpeg')] bg-cover bg-center opacity-50 group-hover:scale-105 group-hover:opacity-60 transition-all duration-700" />
              <div className="relative z-20">
                <Sparkles className="text-[#D4AF37] mb-4" size={36} />
                <h3 className="font-serif text-3xl text-white mb-2">Sur Mesure</h3>
                <p className="text-white/70 font-light mb-4">Montures percées & créations personnalisées</p>
                <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm uppercase tracking-wider group-hover:gap-4 transition-all">
                  Créer <ArrowRight size={16} />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="py-20 bg-[#1A1A1A]">
        <div className="container-premium">
          <div className="text-center mb-14">
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] mb-3 font-medium">Nos Services</p>
            <h2 className="font-serif text-3xl md:text-4xl font-normal tracking-tight text-white">
              Une expertise à votre service
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Service 1 */}
            <div data-testid="service-visagism" className="p-8 bg-[#252525] border border-[#333] hover:border-[#D4AF37] transition-colors duration-500 group">
              <div className="w-12 h-12 flex items-center justify-center border border-[#D4AF37] mb-5 group-hover:bg-[#D4AF37] transition-colors duration-500">
                <Sparkles size={ICON_SIZE.LARGE} className="text-[#D4AF37] group-hover:text-white transition-colors duration-500" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-xl mb-3 text-white">Conseil Visagiste</h3>
              <p className="text-white/60 font-light leading-relaxed text-sm">
                Une analyse personnalisée pour trouver la monture qui sublime votre visage.
              </p>
            </div>

            {/* Service 2 */}
            <div data-testid="service-rimless" className="p-8 bg-[#252525] border border-[#333] hover:border-[#D4AF37] transition-colors duration-500 group">
              <div className="w-12 h-12 flex items-center justify-center border border-[#D4AF37] mb-5 group-hover:bg-[#D4AF37] transition-colors duration-500">
                <Eye size={ICON_SIZE.LARGE} className="text-[#D4AF37] group-hover:text-white transition-colors duration-500" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-xl mb-3 text-white">Montures Percées</h3>
              <p className="text-white/60 font-light leading-relaxed text-sm">
                Spécialiste des montures percées pour un look épuré et raffiné.
              </p>
            </div>

            {/* Service 3 */}
            <div data-testid="service-custom" className="p-8 bg-[#252525] border border-[#333] hover:border-[#D4AF37] transition-colors duration-500 group">
              <div className="w-12 h-12 flex items-center justify-center border border-[#D4AF37] mb-5 group-hover:bg-[#D4AF37] transition-colors duration-500">
                <Wrench size={ICON_SIZE.LARGE} className="text-[#D4AF37] group-hover:text-white transition-colors duration-500" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-xl mb-3 text-white">Sur-Mesure</h3>
              <p className="text-white/60 font-light leading-relaxed text-sm">
                Personnalisation complète de vos lunettes, du verre à la monture.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Specialty Section */}
      <section data-testid="specialty-section" className="py-20 bg-[#F0F0EB]">
        <div className="container-premium">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] mb-3 font-medium">Notre Spécialité</p>
              <h2 className="font-serif text-3xl md:text-4xl font-normal tracking-tight mb-6 text-[#1A1A1A]">
                Montures Percées & Montages Traditionnels
              </h2>
              <p className="text-base font-light text-[#666660] leading-relaxed mb-6">
                Expert en montures percées pour une légèreté absolue, nous réalisons 
                également tous types de montages traditionnels. Quel que soit votre style, 
                nous avons la solution adaptée.
              </p>
              
              <ul className="space-y-3 mb-8">
                {[
                  "Montures percées ultra-légères",
                  "Montages traditionnels soignés",
                  "Personnalisation complète",
                  "Finitions premium"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-[#D4AF37]/10 rounded-full">
                      <Check size={12} className="text-[#D4AF37]" />
                    </div>
                    <span className="text-[#666660] font-light text-sm">{item}</span>
                  </li>
                ))}
              </ul>

              <a
                href="https://wa.me/33781477380"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="specialty-whatsapp"
                className="inline-flex items-center gap-3 bg-[#25D366] text-white px-6 py-3 text-xs uppercase tracking-[0.15em] font-medium hover:bg-[#1da954] transition-colors duration-300"
              >
                Nous contacter sur WhatsApp
              </a>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="image-zoom">
                <img
                  src="https://customer-assets.emergentagent.com/job_lunettes-sur-mesure/artifacts/4cgaqewx_7A1896D3-DE69-4135-BD12-C9F61C2DE6C7.JPG"
                  alt="Boîte Cartier"
                  className="w-full h-[280px] object-cover"
                />
              </div>
              <div className="image-zoom mt-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_lunettes-sur-mesure/artifacts/0imk12wa_IMG_4921.jpeg"
                  alt="Lunettes Cartier bois précieux"
                  className="w-full h-[280px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brands Strip - Marquee */}
      <section data-testid="brands-section" className="py-10 bg-[#1A1A1A] overflow-hidden">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-center text-white/40 font-medium">Nos marques partenaires</p>
        </div>
        <div className="relative">
          <div className="flex animate-marquee whitespace-nowrap">
            {["Fred", "Cartier", "Dior", "Renaissance", "Tom Ford", "Gucci", "Ray-Ban", "Fendi", "Bali", "Andybrook", "Eye Five", "Saint Laurent", "Chloé", "Prada", "Loewe", "Miu Miu", "Céline", "Fred", "Cartier", "Dior", "Renaissance", "Tom Ford", "Gucci", "Ray-Ban", "Fendi", "Bali", "Andybrook", "Eye Five", "Saint Laurent", "Chloé", "Prada", "Loewe", "Miu Miu", "Céline"].map((brand, idx) => (
              <span
                key={`brand-${brand}-${idx}`}
                className="font-serif text-lg md:text-xl text-white/40 mx-8 md:mx-12 hover:text-[#D4AF37] transition-colors duration-300 cursor-default"
              >
                {brand}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <section data-testid="instagram-section" className="py-20 bg-[#F9F9F7]">
        <div className="container-premium">
          <div className="text-center mb-10">
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] mb-3 font-medium">Suivez-nous</p>
            <h2 className="font-serif text-3xl md:text-4xl font-normal tracking-tight text-[#1A1A1A] mb-4">
              @opticien_vaires
            </h2>
            <p className="text-[#666660] font-light max-w-lg mx-auto">
              Découvrez nos dernières créations et l&apos;envers du décor de notre boutique sur Instagram
            </p>
          </div>

          {/* Instagram Screenshot */}
          <div className="max-w-xs mx-auto mb-10">
            <a
              href="https://instagram.com/opticien_vaires"
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_lunettes-sur-mesure/artifacts/p1xp1g3b_copy_A2CB7CCA-B757-4F9F-8167-B20A98D5F0A9.JPEG"
                alt="Instagram @opticien_vaires"
                className="w-full h-auto"
              />
            </a>
          </div>

          {/* Instagram CTA */}
          <div className="text-center">
            <a
              href="https://instagram.com/opticien_vaires"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-follow-btn"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] text-white px-8 py-4 text-xs uppercase tracking-[0.15em] font-medium hover:opacity-90 transition-opacity duration-300"
            >
              <Instagram size={18} />
              Suivre sur Instagram
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-20 bg-[#1A1A1A]">
        <div className="container-premium">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-normal tracking-tight mb-5 text-white">
              Prêt à sublimer votre regard ?
            </h2>
            <p className="text-base font-light text-white/60 mb-10">
              Prenez rendez-vous pour une consultation personnalisée et découvrez 
              notre sélection de montures premium.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <a
                href="https://wa.me/33781477380"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp"
                className="inline-flex items-center gap-3 bg-[#25D366] text-white px-6 py-3 text-xs uppercase tracking-[0.15em] font-medium hover:bg-[#1da954] transition-colors duration-300"
              >
                Nous contacter sur WhatsApp
              </a>
              <Link
                to="/contact"
                data-testid="cta-rdv"
                className="inline-flex items-center gap-3 bg-transparent text-white px-6 py-3 text-xs uppercase tracking-[0.15em] font-medium border border-white/30 hover:bg-white hover:text-[#1A1A1A] transition-colors duration-300"
              >
                Prendre rendez-vous
              </Link>
            </div>
            {/* Instagram */}
            <a
              href="https://instagram.com/opticien_vaires"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-instagram"
              className="inline-flex items-center gap-2 text-white/50 hover:text-[#D4AF37] transition-colors duration-300 text-sm"
            >
              <Instagram size={18} />
              <span>@opticien_vaires</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
